import React from "react";
import {Button, Card, CardBody, CardHeader, Input, Toast} from "gd-react";
import gatewayCommandSocket from "../../services/gatewayCommandSocket";

const IPV4_RE = /^(25[0-5]|2[0-4]\d|[01]?\d?\d)(\.(25[0-5]|2[0-4]\d|[01]?\d?\d)){3}$/;
const INFO_METHOD = 'gateway.info';
// Long enough for the saved config to reach the hub and be applied before it is asked again
const REREAD_AFTER_SAVE_MS = 5000;

// The hub's eth0 as gateway.info reports it, in the form's shape; null is DHCP
function formFromDevice(settings) {
    return {address: settings.address || '', gateway: settings.gateway || '', dns: settings.dns || ''};
}

function sameEthernet(deviceSettings, form) {
    if (!deviceSettings) return !form.address;
    return deviceSettings.address === form.address
        && (deviceSettings.gateway || '') === form.gateway
        && (deviceSettings.dns || '') === form.dns;
}

export function describeEthernet(settings) {
    if (!settings) return 'DHCP';
    return 'Static ' + settings.address
        + (settings.gateway ? ' · gateway ' + settings.gateway : '')
        + (settings.dns ? ' · DNS ' + settings.dns : '');
}

function ethernetFromItem(item) {
    let s = item && item.staticEthernetSettings ? item.staticEthernetSettings : {};
    return {
        address: s.address || '',
        gateway: s.gateway || '',
        dns: s.dns || ''
    };
}

class GatewayNetwork extends React.Component {
    constructor(props) {
        super(props);
        this.updatedExternally = this.updatedExternally.bind(this);
        this.transmitEthernetUpdate = this.transmitEthernetUpdate.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.state = {
            ethernet: ethernetFromItem(props.item),
            // eth0 as the hub has it right now, which a change made on site can leave different from the saved settings
            device: null,
            triedToSave: false,
            errors: {},
            open: false
        };
        if (props.item) {
            props.item.on('updated', this.updatedExternally);
        }
    }

    componentDidMount() {
        const self = this;
        gatewayCommandSocket.capabilities(this.props.item.id)
            .then(function (res) {
                if (self.unmounted) return;
                if ((res.methods || []).indexOf(INFO_METHOD) !== -1) return self.readDevice();
            })
            // An offline hub just shows the saved settings, as this page always did
            .catch(function () {});
    }

    readDevice() {
        const self = this;
        return gatewayCommandSocket.invoke(this.props.item.id, INFO_METHOD)
            .then(function (info) {
                if (self.unmounted || !info || !info.ethernet) return;
                const device = info.ethernet;
                const saved = ethernetFromItem(self.props.item);
                const update = {device: device};
                // Shown in the form, so a plain Save brings the saved settings in line with the hub instead of hiding the difference
                if (device.settings && !sameEthernet(device.settings, saved)) update.ethernet = formFromDevice(device.settings);
                self.setState(update);
            })
            .catch(function () {});
    }

    componentDidUpdate(prevProps) {
        if (prevProps.item !== this.props.item) {
            if (prevProps.item) prevProps.item.off('updated', this.updatedExternally);
            this.props.item.on('updated', this.updatedExternally);
            this.updatedExternally();
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
        clearTimeout(this.rereadTimer);
        if (this.props.item && this.props.item.off) {
            this.props.item.off('updated', this.updatedExternally);
        }
    }

    updatedExternally() {
        this.setState({ethernet: ethernetFromItem(this.props.item)});
    }

    transmitEthernetUpdate() {
        let e = this.state.ethernet;
        let errors = {};
        if (!IPV4_RE.test(e.address)) errors.address = true;
        if (!IPV4_RE.test(e.gateway)) errors.gateway = true;
        if (!IPV4_RE.test(e.dns)) errors.dns = true;
        if (Object.keys(errors).length) {
            this.setState({triedToSave: true, errors: errors});
            return Promise.reject();
        }
        return this.props.item.set({
            staticEthernetSettings: {
                address: e.address,
                gateway: e.gateway,
                dns: e.dns
            }
        }).then(() => {
            this.setState({triedToSave: false, errors: {}, open: true});
            if (this.state.device) {
                clearTimeout(this.rereadTimer);
                this.rereadTimer = setTimeout(() => this.readDevice(), REREAD_AFTER_SAVE_MS);
            }
        });
    }

    handleClose() {
        this.setState({open: false});
    }

    render() {
        let self = this;
        let ethernet = this.state.ethernet;
        let errors = this.state.errors;
        let triedToSave = this.state.triedToSave;
        let device = this.state.device;
        let differs = device && !sameEthernet(device.settings, ethernetFromItem(this.props.item));
        return (
            <div className={"page grey narrow not-flex"}>
                <div className={'column top'}>
                    <div className={'detail-content'}>
                        <Card>
                            <CardHeader title={'Pi-Duck Static Ethernet'}/>
                            <CardBody>
                                {device ? (
                                    <div className={'row'} style={{marginBottom: '15px', display: 'block'}}>
                                        <p style={{margin: 0}}><strong>On the device now:</strong> {describeEthernet(device.settings)}</p>
                                        {differs ? (
                                            <p style={{margin: '4px 0 0'}}>
                                                {device.source === 'local' ? 'Changed on site, from the hub\'s status page. ' : 'This differs from the saved settings. '}
                                                {device.settings
                                                    ? 'The form shows the hub\'s settings: saving keeps the dashboard in step, and changing them here replaces them on the hub.'
                                                    : 'The hub is on DHCP. Saving a static IP here moves it back to static.'}
                                            </p>
                                        ) : null}
                                    </div>
                                ) : null}
                                <div className={'row'} style={{marginBottom: '15px'}}>
                                    <Input
                                        top='true'
                                        label={'IP Address'}
                                        name={'static-ethernet-address'}
                                        placeholder={'e.g. 192.168.1.79'}
                                        value={ethernet.address}
                                        error={triedToSave && errors.address}
                                        onChange={(e) => self.setState({
                                            ethernet: {...ethernet, address: e.target.value}
                                        })}
                                    />
                                </div>
                                <div className={'row'} style={{marginBottom: '15px'}}>
                                    <Input
                                        label={'Gateway'}
                                        name={'static-ethernet-gateway'}
                                        placeholder={'e.g. 192.168.1.1'}
                                        value={ethernet.gateway}
                                        error={triedToSave && errors.gateway}
                                        onChange={(e) => self.setState({
                                            ethernet: {...ethernet, gateway: e.target.value}
                                        })}
                                    />
                                </div>
                                <div className={'row'} style={{marginBottom: '15px'}}>
                                    <Input
                                        label={'DNS'}
                                        name={'static-ethernet-dns'}
                                        placeholder={'e.g. 192.168.1.1'}
                                        value={ethernet.dns}
                                        error={triedToSave && errors.dns}
                                        onChange={(e) => self.setState({
                                            ethernet: {...ethernet, dns: e.target.value}
                                        })}
                                    />
                                </div>
                                <Button
                                    onClick={this.transmitEthernetUpdate}
                                    progressRes
                                    additionalclasses={'sm'}
                                    color={'gd-green'}
                                    label={'Save'}
                                />
                            </CardBody>
                        </Card>
                    </div>
                </div>
                <Toast onClose={this.handleClose} message={"Save Successful"}
                       anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                       open={this.state.open}/>
            </div>
        );
    }
}

export default GatewayNetwork;
